/**
 * @file This file contains components for rendering individual player events within the match commentary log.
 * @author Alun / Tony Gem
 */

import {
  PlayerRoundEvents,
  PlayerRoundState,
  RoundState,
} from '@alienheadwars/football-card-manager-model';
import { localise } from '../../../../../locale/locale';
import { neutralColours } from '../../../../cards/CardColours';
import { InLine } from '../../../../cards/playerCards/CardDimensions';
import { ExpandablePlayerCard } from '../../../../cards/playerCards/ExpandablePlayerCard';
import { CommentaryCard } from './CommentaryCard';
import { CommentaryTypography } from './CommentaryTypography';

/**
 * A set of player events that should override the generic 'DISCARDED' event
 * to provide more specific and useful commentary to the player.
 */
const DISCARD_OVERRIDE_EVENTS = new Set([
  PlayerRoundEvents.SOUL_SAP_1,
  PlayerRoundEvents.SOUL_SAP_2,
]);

/**
 * Filters a player's round events to ensure only the most specific commentary is shown.
 * If an event exists that overrides the generic discard message, the generic one is removed.
 * @param {PlayerRoundEvents[]} events - The array of events for a single player in a round.
 * @returns {PlayerRoundEvents[]} The filtered array of events to be rendered.
 */
const filterRedundantEvents = (events: PlayerRoundEvents[]): PlayerRoundEvents[] => {
  const hasOverride = events.some(event => DISCARD_OVERRIDE_EVENTS.has(event));
  if (hasOverride) {
    return events.filter(event => event !== PlayerRoundEvents.DISCARDED);
  }
  return events;
};


/**
 * Props for the PlayerEventCommentaries component.
 * @property {RoundState} roundState - The state of the current round, containing player hands and events.
 */
export interface PlayerEventCommentariesProps {
  roundState: RoundState;
}

/**
 * Renders a list of all player-specific events (like discards) that occurred in a round.
 * It iterates through both the attacking and defending hands, filters for redundant events,
 * and then displays the most specific commentary.
 * @param {PlayerEventCommentariesProps} props - The props for the component.
 * @returns {React.ReactElement[]} An array of PlayerEvent components.
 */
export const PlayerEventCommentaries = (
  props: PlayerEventCommentariesProps
) => {
  const allPlayers = [
    ...props.roundState.attackingHand,
    ...props.roundState.defendingHand,
  ];

  const allEvents = allPlayers.flatMap((player) => {
    const filteredEvents = filterRedundantEvents(player.roundEvents);
    return filteredEvents.map((event, index) => (
      <PlayerEvent
        key={`${player.playerDetails.id}-${event}-${index}`}
        player={player}
        event={event}
      />
    ));
  });

  return <>{allEvents}</>;
};

/**
 * Props for the PlayerEvent component.
 * @property {PlayerRoundState} player - The state of the player for the round.
 * @property {PlayerRoundEvents} event - The specific event that occurred to the player.
 */
export interface PlayerEventProps {
  player: PlayerRoundState;
  event: PlayerRoundEvents;
}
/**
 * Renders a single commentary line for a specific player event.
 * @param {PlayerEventProps} props - The props for the component.
 * @returns {React.ReactElement} A commentary card detailing the player event.
 */
export const PlayerEvent = (props: PlayerEventProps) => {
  const { player, event } = props;
  return (
    <CommentaryCard colours={neutralColours}>
      <ExpandablePlayerCard
        player={player.playerDetails}
        dimensions={InLine}
      />
      <CommentaryTypography>
        {localise(`commentary.playerRoundEvent.${event}`)}
      </CommentaryTypography>
    </CommentaryCard>
  );
};